import React, { useEffect, useState } from 'react';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { useNavigate } from 'react-router-dom';
import './Login.css';
import { useDocumentTitle } from '../lib/useDocumentTitle';

/**
 * Rendered the moment Amplify reports a session. It redirects immediately
 * rather than pausing on a confirmation screen — the old version waited a
 * second and printed `user.username`, which on an email-alias pool is the raw
 * Cognito sub (a UUID), so a stranger's-looking id flashed on every sign-in.
 */
const RedirectOnSignIn: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    navigate('/', { replace: true });
  }, [navigate]);

  return (
    <div className="logged-in-message">
      <h3>Signed in</h3>
      <p>Taking you to the blog…</p>
    </div>
  );
};

/**
 * Puts the name field first and labels it as what it is — the name that will
 * appear on anything the person publishes. Amplify's default label is just
 * "Name", which gives no hint that it is public.
 */
const formFields = {
  signUp: {
    name: {
      order: 1,
      label: 'Name',
      placeholder: 'How your name appears on your posts',
      isRequired: true,
    },
    email: { order: 2 },
    password: { order: 3 },
    confirm_password: { order: 4 },
  },
};

/**
 * The password policy, shown before the person types rather than after they
 * submit.
 *
 * Amplify only surfaces the rules as an error once Cognito has rejected the
 * attempt, so the first a new user hears of them is a failure — having already
 * chosen a password, filled the form, and pressed the button.
 *
 * ⚠️ These must mirror the pool's actual policy (Cognito → User pool →
 * Sign-up → Password policy). Listed here because the deploy credentials
 * cannot read it. Below is Cognito's default; if the pool has been relaxed,
 * these hints are merely stricter than necessary, which is the safe direction
 * to be wrong in.
 */
const PASSWORD_RULES: Array<{ label: string; ok: (v: string) => boolean }> = [
  { label: 'At least 8 characters', ok: (v) => v.length >= 8 },
  { label: 'A lowercase letter', ok: (v) => /[a-z]/.test(v) },
  { label: 'An uppercase letter', ok: (v) => /[A-Z]/.test(v) },
  { label: 'A number', ok: (v) => /\d/.test(v) },
  { label: 'A symbol, such as ! ? or #', ok: (v) => /[^A-Za-z0-9]/.test(v) },
];

/**
 * Amplify's own fields, followed by a live checklist.
 *
 * The wrapper reads the password by listening for change events as they
 * bubble, rather than trying to control inputs Amplify owns — React normalises
 * `input` events to `onChange` and they propagate like any other. The wrapper
 * is `display: contents` so it adds a listener without adding a box, leaving
 * Amplify's own layout untouched.
 */
function SignUpFormFields(): React.JSX.Element {
  const [password, setPassword] = useState('');

  return (
    <div
      className="signup-fields"
      onChange={(e) => {
        const target = e.target as HTMLInputElement;
        if (target.name === 'password') setPassword(target.value);
      }}
    >
      <Authenticator.SignUp.FormFields />

      <ul className="password-rules" aria-label="Password requirements">
        {PASSWORD_RULES.map(({ label, ok }) => {
          const met = ok(password);
          return (
            <li key={label} className={met ? 'met' : ''}>
              <span aria-hidden="true">{met ? '✓' : '○'}</span>
              <span>{label}</span>
              {/* Announced only on the transition, so a screen reader is not
                  read the entire list again on every keystroke. */}
              <span className="sr-only">{met ? ' — met' : ''}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const components = {
  SignUp: {
    FormFields: SignUpFormFields,
  },
};

const Login: React.FC = () => {
  useDocumentTitle('Sign in');

  return (
    <div className="login-container">
      <div className="login-content-wrapper">
          <div className="login-header">
            <h2>Sign in to QED AI</h2>
            <p>Publish your own posts, or pick up where you left off.</p>
          </div>

          {/*
            `signUpAttributes` is what puts a Name field on the sign-up form.
            Without it Cognito receives no `name` attribute, the user row is
            written with an empty one, and every display of that person falls
            back to whatever the token offers — which on this pool is the sub.
            Collecting it once at sign-up is the fix; the backend fallback is
            only the safety net.
          */}
          <Authenticator
            signUpAttributes={['name']}
            formFields={formFields}
            components={components}
          >
            {({ user }) => (user ? <RedirectOnSignIn /> : <></>)}
          </Authenticator>
      </div>
    </div>
  );
};

export default Login;
